import { useEffect, useState, useContext } from 'react'
import { Form, Typography, Row, Col, Button, Alert, Spin} from "antd"
import { Selector } from 'antd-mobile'
import { WarningFilled, DownOutlined, StarOutlined, EnvironmentOutlined, CheckOutlined } from '@ant-design/icons'
import "./facility.scss"
import { FlowStepFooter } from '../../flowStep/flowStepFooter.component'
import { getNearbyFacilities } from '../../../../services/facility.service'
import { getProductTypeByCode } from '../../../../services/productType.service'
import ProductHelper from '../../../../helpers/product.helper'
import { UserContext } from '../../../../contexts/user.context'
import PayTo from '../../../../enums/payTo.enum'
const { Item } = Form
const { Text } = Typography

export const Facility = ({
  step,
  flow,
  onSubmit,
  form,
  isSubmitting,
  onNextStep
}) => {
  const fieldWatch = Form.useWatch(step?.field?.name, form) 
  const [rules, setRules] = useState([])
  const [location, setLocation] = useState()
  const [facilities, setFacilities] = useState([])
  const [visibleFacilities, setVisibleFacilities] = useState(3) 
  const [noFacilitiesFound, setNoFacilitiesFound] = useState(false)
  const [isLoading, setIsLoading] = useState(false); // Add loading state
  const [productTypes, setProductTypes] = useState([])
  const [addendumProductType, setAddendumProductType] = useState(null)
  const { instalabMembership, currentUser } = useContext(UserContext)

  useEffect(() => {
    const keyDownHandler = event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        onSubmit()
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [onSubmit]);

  useEffect(() => {
    const fetchProductTypes = async () => {
      if (step?.productTypeCode) {
        const productTypeCodes = Array.isArray(step.productTypeCode) ? step.productTypeCode : [step.productTypeCode]
        const productTypes = await Promise.all(productTypeCodes.map(async code => await getProductTypeByCode(code)))
        setProductTypes(productTypes)
      }
    }

    const fetchAddendumProductType = async () => {
      if (step?.addendumProductTypeCode) {
        const addendumProductType = await getProductTypeByCode(step.addendumProductTypeCode)
        setAddendumProductType(addendumProductType)
      }
    }

    if (step) {
      fetchRules();
      fetchProductTypes()
      fetchAddendumProductType()
    }
  }, [step])

  useEffect(() => {
    if (flow) {
      fetchLocation();
    }
  }, [flow])

  useEffect(() => {
    if (location) {
      fetchClosestFacilities();
    }
  }, [location]);


  const getProductType = (code) => {
    return productTypes.find(type => type.code === code)
  }

  const fetchLocation = () => {
    setLocation(flow?.appointment?.location || flow?.user?.location);
  }

  const fetchRules = () => {
    if (!step?.field) return;
    setRules([{
      required: true,
      message: <><WarningFilled />&nbsp; Select an option.</>,
    }]);
  }

  const fetchClosestFacilities = async () => {
    if (!location || !step?.productTypeCode) return;
    setIsLoading(true); // Show loader

    try {
      const { latitude, longitude } = location

      let facilityData= await getNearbyFacilities({
        latitude: latitude, 
        longitude: longitude, 
        productTypeCode: step.productTypeCode
      });

      setIsLoading(false); // Hide loader

      // filter out facilities that don't have price if showPrice is true
      const productTypeCodes = Array.isArray(step.productTypeCode) ? step.productTypeCode : [step.productTypeCode]
      
      if (step.showPrice) {
        facilityData = facilityData.filter(facility => facility.menu.find(item => productTypeCodes.includes(item.productType.code) && item.price));
      }

      if (!facilityData || !Array.isArray(facilityData) || facilityData.length === 0) {
        setNoFacilitiesFound(true);  
      } else {
        setNoFacilitiesFound(false);
        setFacilities(facilityData);  
      }
    } catch (error) {
      console.error("Error fetching nearby facilities:", error);
      setIsLoading(false); // Hide loader in case of error
    }
  };

 
  // Handle displaying more facilities (3 at a time)
  const handleLoadMore = () => {
    setVisibleFacilities(prevVisible => prevVisible + 3);
  }

  const findBestValueOptions = (facilities) => {
    // First filter for nearby facilities
    const nearbyOptions = facilities.filter(option => option.distance <= 20);
    if (nearbyOptions.length === 0) return new Set();
  
    // Helper function to find matching menu item
    const findMatchingMenuItem = (facility) => {
      return facility.menu.find(item => 
        step.productTypeCode.includes(item.productType.code)
      );
    };
  
    // Get valid facilities with their prices
    const validFacilities = nearbyOptions
      .map(facility => {
        const menuItem = findMatchingMenuItem(facility);
        return {
          facilityId: facility._id,
          price: menuItem?.price,
          payTo: menuItem?.payTo,
          distance: facility.distance
        };
      })
      .filter(facility => 
        facility.payTo === PayTo.INSTALAB && 
        typeof facility.price === 'number'
      )

  
    if (validFacilities.length === 0) return new Set();
  
    // Find cheapest price
    const cheapestPrice = Math.min(
      ...validFacilities.map(facility => Math.pow(facility.price,2) * (facility.distance < 1 ? 1 : facility.distance))
    );

  
    // Return facilities with cheapest price
    return new Set(
      validFacilities
        .filter(facility => Math.pow(facility.price,2) * (facility.distance < 1 ? 1 : facility.distance) === cheapestPrice)
        .map(facility => facility.facilityId)
        .slice(0, 1)
    );
  };

  // Facilities to display (first 3, then load more)
  const displayedFacilities = facilities.slice(0, visibleFacilities);

  // Create options from facilities to display
  const renderOptions = (displayedFacilities) => {
    return displayedFacilities.map((facility) => {

      let price = 0;

      if (step.productTypeCode) {
        const productTypeCodes = Array.isArray(step.productTypeCode) ? step.productTypeCode : [step.productTypeCode]

        for (const productTypeCode of productTypeCodes) {
          const menu = facility.menu || null
          const menuItem = menu.find(item => item.productType.code === productTypeCode)
          if (menuItem) {
            price += menuItem.price
          }

          // this is another product to add to price
          // else {
          //   price += ProductHelper.getTotalCostV2({
          //     productTypes: [getProductType(productTypeCode)],
          //     instalabMembership,
          //     currentUser,
          //   })
          // }
        }

        if (addendumProductType) {
          price += ProductHelper.getTotalCostV2({
          productTypes: [addendumProductType],
          instalabMembership,
          currentUser,
        })
      }


      }


      return {
        value: facility._id || facility.questId,
        label: (
          <div className="facility-select-label">
            <Row>
              <Col className="col-1">
                <div className="facility-select-abbr">
                  <EnvironmentOutlined className="facility-select-not-checked" /> <CheckOutlined className="facility-select-check"/>
                </div>
              </Col> 
              <Col className="col-2">
                <div className="option-title">
                  {facility.name}  
                </div>
                <div className="option-description">
                { (step.showPrice && price) && <span className="option-drive-time">
                    {Number(facility.distance).toFixed(1)}  mi away{" "}•{" "}</span>}
                  {facility.location.streetAddress ? `${facility.location.streetAddress}, ` : ''} 
                  {facility.location.city ? `${facility.location.city}, ` : ''} 
                  {facility.location.state ? facility.location.state : ' '}
                </div>   
            
                <Text className="option-price">
                  { (step.showPrice && price) ? `$${Math.round(price,0)}` : `${Number(facility.distance).toFixed(1)}  mi`}
                </Text>

                {findBestValueOptions(facilities).has(facility._id) && <div className="option-tag">
                  <StarOutlined /> Most Popular
                </div>}
                        
              </Col>
            </Row>
          </div>
        )
      }
    })
  }

  const selectorOptions = renderOptions(displayedFacilities)

  return (
    facilities &&  <div className="facility-select">
       {isLoading ? ( // Display loader while fetching facilities
        <div style={{ textAlign: 'left', padding: '20px 0' }}>
          <Spin size="large" />
        </div>
      ) : noFacilitiesFound ? (
        <Alert 
          type="info"
          message={<>We don’t have any nearby facilities in our system right now, but no worries—we can still help! Email <a href="mailto:concierge@instalab.com">concierge@instalab.com</a>, and we’ll personally assist you in finding one.</>}
          ></Alert>
      ) :
        selectorOptions.length > 0 && <Form 
          form={form}
          className="facility-select-form"
        >
          <Item 
            key={`field-${step.field.name}`}
            name={step.field.name}
            className="facility-select-item"
            validateTrigger='onSubmit'
            rules={rules}
          >
            <Selector
              onChange={(value) => {
                form.setFieldValue(step.field.name, value); 
                if (value?.length && !step.multiple) {
                  onSubmit()
                }
              }}
              multiple={false}
              disabled={step.isLocked && step.isLocked()}
              options={selectorOptions}
              className="facility-select-field"
            />
          </Item>

        {/* Load more button (shown if more facilities exist) */}
        {facilities.length > visibleFacilities && (
          <div className="load-more-container">
            <Button 
              type="default" 
              onClick={handleLoadMore} 
              className="load-more-button"
              size="large"
            >
             View more <DownOutlined/>
            </Button>
          </div>
        )}


          {(fieldWatch?.length > 0) && (
            <FlowStepFooter 
              step={step}
              onSubmit={() => onSubmit()} 
              isSubmitting={isSubmitting}
              buttonText={step.buttonText}
              onNextStep={onNextStep}
              hasSkip={step.hasSkip}
            />
          )}
        </Form>}
      
    </div>
  )
}